import React, { useState, useEffect } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import {
  Card,
  Col,
  Container,
  Row,
  CardBody,
  Button,
  Alert,
} from "reactstrap";

const baseUrl = process.env.REACT_APP_BASE_URL;

const PrtPayouts = () => {
  document.title = "Payouts | BizServe";
  
  const [isLoading, setIsLoading] = useState(false);
  const [isCheckingAccount, setIsCheckingAccount] = useState(true);
  const [hasStripeAccount, setHasStripeAccount] = useState(false);
  const [alertMsgg, setAlertMsgg] = useState<any>({
    open: false,
    type: "success",
    content: "Messages here.",
    time: "2500"
  });

  const handleAlertMsggClose = (event?: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === "clickaway") {
      return;
    }
    setAlertMsgg({ ...alertMsgg, open: false });
  };

  useEffect(() => {
    const checkStripeAccount = async () => {
      try {
        setIsCheckingAccount(true);
        const response = await axios.post(`${baseUrl}/accounts/get_partner_settings`, {});
        if (response.status === "SENT" && response.partner) {
          const stripeConnect = response.partner.stripe_connect;
          if (stripeConnect && stripeConnect.stripe_account_id) {
            setHasStripeAccount(true);
          } else {
            setHasStripeAccount(false);
          }
        }
      } catch (error: any) {
        console.error("Error checking Stripe account:", error);
        setHasStripeAccount(false);
      } finally {
        setIsCheckingAccount(false);
      }
    };
    checkStripeAccount();
  }, []);

  const handleAccessStripeDashboard = async () => {
    try {
      setIsLoading(true);
      const response = await axios.post(`${baseUrl}/accounts/create_stripe_login_link`, {});
      
      if (response.status === "SENT" && response.login_url) {
        // Open Stripe Express Dashboard in a new tab
        window.open(response.login_url, '_blank', 'noopener,noreferrer');
        setIsLoading(false);
      } else {
        setIsLoading(false);
        // Check if error is about no Stripe account
        if (response.error && response.error.includes("No Stripe Connect account found")) {
          setHasStripeAccount(false);
        } else {
          setAlertMsgg({
            ...alertMsgg,
            open: true,
            content: response.error || "Error creating login link",
            type: "error"
          });
        }
      }
    } catch (error: any) {
      setIsLoading(false);
      if (error.response) {
        // Check if error is about no Stripe account
        if (error.response.data?.error && error.response.data.error.includes("No Stripe Connect account found")) {
          setHasStripeAccount(false);
        } else if ([401, 422].includes(error.response.status)) {
          setAlertMsgg({
            ...alertMsgg,
            open: true,
            content: error.response.data.error || "Error creating Stripe login link",
            type: "error"
          });
        } else if (error.response.status === 403) {
          setAlertMsgg({
            ...alertMsgg,
            open: true,
            content: "Session expired!",
            type: "error"
          });
          setTimeout(() => {
            window.location.href = "/logout";
          }, 2000);
        } else {
          setAlertMsgg({
            ...alertMsgg,
            open: true,
            content: "Error creating Stripe login link",
            type: "error"
          });
        }
      } else {
        setAlertMsgg({
          ...alertMsgg,
          open: true,
          content: "Network error",
          type: "error"
        });
      }
    }
  };

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <Row>
            <Col lg="12">
              <Card>
                <CardBody>
                  {isCheckingAccount ? (
                    <div className="text-center py-5">
                      <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Loading...</span>
                      </div>
                      <p className="mt-3 text-muted">Checking your account...</p>
                    </div>
                  ) : !hasStripeAccount ? (
                    <div className="text-center py-5">
                      <div className="mb-4">
                        <i className="bx bx-info-circle font-size-48 text-warning"></i>
                      </div>
                      <h4 className="mb-3">No Stripe Account Found</h4>
                      <p className="text-muted mb-4">
                        You need to connect your Stripe account before you can view your payouts.
                      </p>
                      <div className="alert alert-info mx-auto" style={{ maxWidth: "600px" }}>
                        <h5 className="alert-heading mb-3">How to Connect Your Stripe Account:</h5>
                        <ol className="text-start mb-0" style={{ paddingLeft: "1.5rem" }}>
                          <li className="mb-2">Go to your <strong>Profile</strong> page</li>
                          <li className="mb-2">Find the <strong>Stripe Connect</strong> section</li>
                          <li className="mb-2">Click on the <strong>"Connect Stripe Account"</strong> button</li>
                          <li className="mb-2">Complete the Stripe onboarding process</li>
                          <li>Once connected, return here to view your payouts</li>
                        </ol>
                      </div>
                      <Link to="/pprofile">
                        <Button
                          color="primary"
                          className="waves-effect waves-light mt-3"
                        >
                          <i className="bx bx-user font-size-16 align-middle me-2"></i>
                          Go to Profile
                        </Button>
                      </Link>
                    </div>
                  ) : (
                    <div className="text-center py-5">
                      <div className="mb-4">
                        <i className="bx bx-money font-size-48 text-primary"></i>
                      </div>
                      <h4 className="mb-3">View Your Payouts</h4>
                      <p className="text-muted mb-4">
                        Access your Stripe Express Dashboard to view all your payouts, transaction history, and account details.
                      </p>
                      <Button
                        color="primary"
                        className="waves-effect waves-light"
                        onClick={handleAccessStripeDashboard}
                        disabled={isLoading}
                      >
                        {isLoading ? (
                          <>
                            <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                            Loading...
                          </>
                        ) : (
                          <>
                            <i className="bx bx-exit font-size-16 align-middle me-2"></i>
                            Access Stripe Dashboard
                          </>
                        )}
                      </Button>
                      <div className="mt-4">
                        <small className="text-muted">
                          You will be redirected to Stripe's secure dashboard where you can view:
                        </small>
                        <ul className="list-unstyled mt-2 text-start" style={{ maxWidth: "400px", margin: "0 auto" }}>
                          <li className="mb-2">
                            <i className="bx bx-check-circle text-success me-2"></i>
                            All payouts (pending, completed, failed)
                          </li>
                          <li className="mb-2">
                            <i className="bx bx-check-circle text-success me-2"></i>
                            Transaction history
                          </li>
                          <li className="mb-2">
                            <i className="bx bx-check-circle text-success me-2"></i>
                            Current balance
                          </li>
                          <li className="mb-2">
                            <i className="bx bx-check-circle text-success me-2"></i>
                            Bank account details
                          </li>
                        </ul>
                      </div>
                    </div>
                  )}
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>

      {/* Alert Message */}
      {alertMsgg.open && (
        <Alert
          color={alertMsgg.type === "error" ? "danger" : "success"}
          isOpen={alertMsgg.open}
          toggle={handleAlertMsggClose}
          className="position-fixed top-0 end-0 m-3"
          style={{ zIndex: 9999, minWidth: "300px" }}
        >
          {alertMsgg.content}
        </Alert>
      )}
    </React.Fragment>
  );
};

export default PrtPayouts;

